import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';
import {
  FlexColumn,
  Flex,
  FlexSpace,
  SvgIcon,
  ListItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  OnSubmitEventVal,
  FilterOption,
} from '@elementary/common-ui/components';
import { Typography, Tooltip } from '@mui/material';
import { ReactComponent as InfoIcon } from '@elementary/common-ui/assets/16x16/info.svg';
import useResourceFilterOptions from '../hooks/useResourceFilterOptions';
import {
  PermissionsAccess,
  CustomFeaturesAccess,
  RoleFormData,
  ResourceFilterGroup,
  ResourceFilter,
} from '../../shared/types';
import PermissionsAccessDropdown from './PermissionsAccessDropdown';
import ResourceFilterGroups from './ResourceFilterGroups';

const environmentItemListStyles = css`
  border: 1px solid ${({ theme }) => theme.palette.common.gray16};
  border-bottom: none;

  &:first-child {
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.palette.common.gray16};
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }
`;

const EnvironmentItemWrapper = styled(FlexSpace)`
  ${environmentItemListStyles}
  align-items: center;
  padding: 16px;
  gap: 16px;
`;

const AccordionEnvironmentItemWrapper = styled.div`
  ${environmentItemListStyles}
  overflow: hidden;

  .MuiAccordion-root {
    box-shadow: none;
    background-color: transparent;
    border-radius: 0 !important;
    border-left: none !important;
    border-right: none !important;

    &:before {
      display: none;
    }
  }

  .MuiAccordionSummary-root {
    border-radius: 0 !important;
    border-left: none !important;
    border-right: none !important;
  }

  .MuiAccordionDetails-root {
    border-radius: 0 !important;
    border-left: none !important;
    border-right: none !important;
  }
`;

const EnvironmentInfo = styled(Flex)`
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const EnvironmentName = styled(Typography)<{ $isGrayedout?: boolean }>`
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $isGrayedout, theme }) =>
    $isGrayedout ? theme.palette.common.gray4 : 'inherit'};
`;

const PermissionSection = styled(FlexColumn)`
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.gray13};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const PermissionRow = styled(Flex)`
  align-items: center;
  gap: 16px;
`;

const FeaturePermissionsList = styled(FlexColumn)`
  border: 1px solid ${({ theme }) => theme.palette.common.gray5};
  border-radius: 8px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.common.white};
`;

const FeaturePermissionRow = styled(ListItem)`
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 16px;
  min-height: 48px;
`;

const SectionLabel = styled(Typography)<{ $secondary?: boolean }>`
  ${({ $secondary, theme }) =>
    $secondary &&
    `
    color: ${theme.palette.common.gray2};
  `}
`;

const SectionLabelWithIcon = styled(Flex)`
  align-items: center;
  gap: 6px;
`;

const InfoIconWrapper = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const CustomAccordionSummary = styled(AccordionSummary)`
  &.MuiAccordionSummary-root {
    padding: 16px;
    min-height: unset;
    height: auto;
    border: none;
    border-radius: 0;
  }

  &.MuiAccordionSummary-root.Mui-expanded {
    border: none;
    border-radius: 0;
  }

  .MuiAccordionSummary-content {
    margin: 0;
  }
`;

type BaseEnvironmentItemProps = {
  environmentId: string;
  environmentName: string;
  warehouseIcon: React.ComponentType;
  index: number;
  defaultAccess: PermissionsAccess;
  disabled?: boolean;
};

type FeatureAccessItem = {
  key: keyof CustomFeaturesAccess;
  label: string;
  description: string;
};

const FEATURE_ACCESS_ITEMS: FeatureAccessItem[] = [
  {
    key: 'assets',
    label: 'Assets',
    description:
      'Access to assets (Tables, BI dashboards, etc.) and related metrics.',
  },
  {
    key: 'tests',
    label: 'Tests',
    description: 'Access to tests and their configurations.',
  },
  {
    key: 'incidents',
    label: 'Incidents',
    description:
      'Access to incidents and related actions (assigning, resolving, etc.).',
  },
  {
    key: 'alert_digest_rules',
    label: 'Alert rules',
    description: 'Access to alert and digest rule management.',
  },
  {
    key: 'pull_requests',
    label: 'Pull requests',
    description: 'Access to pull request related capabilities.',
  },
  {
    key: 'saved_views',
    label: 'Saved views',
    description: 'Access to creating and editing saved views.',
  },
  {
    key: 'ticketing',
    label: 'Ticketing',
    description: 'Access to ticketing related actions (ticket creation, etc.).',
  },
];

const createCustomFeaturesAccess = (
  baseAccess: PermissionsAccess,
): CustomFeaturesAccess => ({
  assets: baseAccess,
  tests: baseAccess,
  incidents: baseAccess,
  alert_digest_rules: baseAccess,
  pull_requests: baseAccess,
  saved_views: baseAccess,
  ticketing: baseAccess,
});

export const SimpleEnvironmentItem = ({
  environmentName,
  warehouseIcon: WarehouseIcon,
  index,
  disabled,
}: BaseEnvironmentItemProps) => {
  const { control } = useFormContext<RoleFormData>();

  return (
    <EnvironmentItemWrapper>
      <Controller
        name={`envPermissions.${index}.featuresAccess`}
        control={control}
        render={({ field }) => (
          <>
            <EnvironmentInfo>
              <SvgIcon width={16}>
                <WarehouseIcon />
              </SvgIcon>

              <EnvironmentName
                $isGrayedout={field.value === PermissionsAccess.HIDDEN}
              >
                {environmentName}
              </EnvironmentName>
            </EnvironmentInfo>

            <PermissionsAccessDropdown
              value={field.value}
              disabled={disabled}
              onChange={field.onChange}
            />
          </>
        )}
      />
    </EnvironmentItemWrapper>
  );
};

const ResourceFilterGroupsController = ({
  field,
  filterOptions,
  defaultAccess,
}: {
  field: {
    value: ResourceFilterGroup[] | undefined;
    onChange: (value: ResourceFilterGroup[]) => void;
  };
  filterOptions: FilterOption[];
  defaultAccess: PermissionsAccess;
}) => {
  const handleAddNewFilterGroup = () => {
    const newGroup: ResourceFilterGroup = {
      access: defaultAccess,
      filters: [],
    };
    field.onChange([...(field.value || []), newGroup]);
  };

  const handleAddFilterToGroup = (
    groupIndex: number,
    val: OnSubmitEventVal,
  ) => {
    const newFilter: ResourceFilter = {
      property: val.property,
      operator: val.operator,
      value: val.value,
    };
    const updatedGroups = (field.value || []).map((group, idx) => {
      if (idx === groupIndex) {
        return {
          ...group,
          filters: [...group.filters, newFilter],
        };
      }
      return group;
    });
    field.onChange(updatedGroups);
  };

  const handleUpdateFilterInGroup = (
    groupIndex: number,
    val: OnSubmitEventVal,
  ) => {
    const updatedGroups = (field.value || []).map((group, idx) => {
      if (idx === groupIndex) {
        return {
          ...group,
          filters: group.filters.map((filter) => {
            if (
              filter.property === val.property &&
              filter.operator === val.operator
            ) {
              return {
                ...filter,
                value: val.value,
              };
            }
            return filter;
          }),
        };
      }
      return group;
    });
    field.onChange(updatedGroups);
  };

  const handleRemoveFilterFromGroup = (
    groupIndex: number,
    filterIndex: number,
  ) => {
    const updatedGroups = (field.value || [])
      .map((group, idx) => {
        if (idx === groupIndex) {
          return {
            ...group,
            filters: group.filters.filter((_, fIdx) => fIdx !== filterIndex),
          };
        }
        return group;
      })
      .filter((group) => group.filters.length > 0);
    field.onChange(updatedGroups);
  };

  const handleChangeGroupAccess = (
    groupIndex: number,
    access: PermissionsAccess,
  ) => {
    const updatedGroups = (field.value || []).map((group, idx) => {
      if (idx === groupIndex) {
        return {
          ...group,
          access,
        };
      }
      return group;
    });
    field.onChange(updatedGroups);
  };

  const handleRemoveFilterGroup = (groupIndex: number) => {
    const updatedGroups = (field.value || []).filter(
      (_, idx) => idx !== groupIndex,
    );
    field.onChange(updatedGroups);
  };

  return (
    <ResourceFilterGroups
      filterGroups={field.value || []}
      filterOptions={filterOptions}
      onAddFilterGroup={handleAddNewFilterGroup}
      onAddFilterToGroup={handleAddFilterToGroup}
      onUpdateFilterInGroup={handleUpdateFilterInGroup}
      onRemoveFilterFromGroup={handleRemoveFilterFromGroup}
      onChangeGroupAccess={handleChangeGroupAccess}
      onRemoveFilterGroup={handleRemoveFilterGroup}
    />
  );
};

export const AccordionEnvironmentItem = ({
  environmentId,
  environmentName,
  warehouseIcon: WarehouseIcon,
  index,
  defaultAccess,
  disabled,
}: BaseEnvironmentItemProps) => {
  const { control, watch, setValue } = useFormContext<RoleFormData>();

  const filterGroupsRaw = watch(`envPermissions.${index}.resourceFilterGroups`);
  const filterGroups = useMemo(() => filterGroupsRaw || [], [filterGroupsRaw]);
  const featuresAccess =
    watch(`envPermissions.${index}.featuresAccess`) || defaultAccess;
  const customFeaturesAccess =
    watch(`envPermissions.${index}.customFeaturesAccess`) || null;
  const resourcesAccess =
    watch(`envPermissions.${index}.defaultResourcePermission`) || defaultAccess;
  const isCustomFeaturesAccess = customFeaturesAccess !== null;

  const summaryValue = useMemo(
    () =>
      !isCustomFeaturesAccess &&
      featuresAccess === resourcesAccess &&
      filterGroups.length === 0
        ? featuresAccess
        : PermissionsAccess.CUSTOM,
    [isCustomFeaturesAccess, featuresAccess, resourcesAccess, filterGroups],
  );

  const isGrayedout = useMemo(() => {
    if (
      !isCustomFeaturesAccess &&
      featuresAccess === PermissionsAccess.HIDDEN &&
      resourcesAccess === PermissionsAccess.HIDDEN &&
      filterGroups.length === 0
    ) {
      return true;
    }
    return Object.values(customFeaturesAccess || {}).every(
      (access) => access === PermissionsAccess.HIDDEN,
    );
  }, [
    isCustomFeaturesAccess,
    featuresAccess,
    resourcesAccess,
    filterGroups,
    customFeaturesAccess,
  ]);

  const { filterOptions } = useResourceFilterOptions({
    environmentId,
  });

  return (
    <AccordionEnvironmentItemWrapper>
      <Accordion
        defaultExpanded={summaryValue === PermissionsAccess.CUSTOM}
        disabled={disabled}
      >
        <CustomAccordionSummary>
          <EnvironmentInfo>
            <SvgIcon width={16}>
              <WarehouseIcon />
            </SvgIcon>
            <EnvironmentName $isGrayedout={isGrayedout}>
              {environmentName}
            </EnvironmentName>
          </EnvironmentInfo>
          <PermissionsAccessDropdown
            value={summaryValue}
            onChange={(value) => {
              setValue(`envPermissions.${index}.featuresAccess`, value, {
                shouldDirty: true,
              });
              setValue(
                `envPermissions.${index}.defaultResourcePermission`,
                value,
                { shouldDirty: true },
              );
              setValue(`envPermissions.${index}.resourceFilterGroups`, [], {
                shouldDirty: true,
              });
              setValue(`envPermissions.${index}.customFeaturesAccess`, null, {
                shouldDirty: true,
              });
            }}
          />
        </CustomAccordionSummary>
        <AccordionDetails>
          <FlexColumn sx={{ gap: '24px' }}>
            <PermissionSection>
              <Controller
                name={`envPermissions.${index}.featuresAccess`}
                control={control}
                render={({ field }) => (
                  <PermissionRow>
                    <SectionLabelWithIcon>
                      <SectionLabel>Features permissions</SectionLabel>
                      <Tooltip
                        title={
                          <>
                            Determines the access level for creating and
                            managing alerts, PRs, and Agent rules
                          </>
                        }
                      >
                        <InfoIconWrapper>
                          <SvgIcon width={16}>
                            <InfoIcon />
                          </SvgIcon>
                        </InfoIconWrapper>
                      </Tooltip>
                    </SectionLabelWithIcon>
                    <SectionLabel $secondary>
                      Features in this environment are
                    </SectionLabel>
                    <PermissionsAccessDropdown
                      value={
                        isCustomFeaturesAccess
                          ? PermissionsAccess.CUSTOM
                          : field.value || defaultAccess
                      }
                      includeCustom
                      onChange={(value) => {
                        if (value === PermissionsAccess.CUSTOM) {
                          const baseAccess =
                            field.value === PermissionsAccess.CUSTOM
                              ? defaultAccess
                              : field.value || defaultAccess;
                          setValue(
                            `envPermissions.${index}.customFeaturesAccess`,
                            createCustomFeaturesAccess(baseAccess),
                            { shouldDirty: true },
                          );
                          return;
                        }

                        field.onChange(value);
                        setValue(
                          `envPermissions.${index}.customFeaturesAccess`,
                          null,
                          { shouldDirty: true },
                        );
                      }}
                    />
                  </PermissionRow>
                )}
              />
              {isCustomFeaturesAccess && customFeaturesAccess && (
                <FeaturePermissionsList>
                  {FEATURE_ACCESS_ITEMS.map((featureAccessItem) => (
                    <FeaturePermissionRow key={featureAccessItem.key}>
                      <SectionLabelWithIcon>
                        <SectionLabel>{featureAccessItem.label}</SectionLabel>
                        <Tooltip title={featureAccessItem.description}>
                          <InfoIconWrapper>
                            <SvgIcon width={16}>
                              <InfoIcon />
                            </SvgIcon>
                          </InfoIconWrapper>
                        </Tooltip>
                      </SectionLabelWithIcon>
                      <PermissionsAccessDropdown
                        value={
                          customFeaturesAccess[featureAccessItem.key] ||
                          PermissionsAccess.HIDDEN
                        }
                        onChange={(value) => {
                          setValue(
                            `envPermissions.${index}.customFeaturesAccess.${featureAccessItem.key}`,
                            value,
                            { shouldDirty: true },
                          );
                        }}
                      />
                    </FeaturePermissionRow>
                  ))}
                </FeaturePermissionsList>
              )}
            </PermissionSection>

            <PermissionSection>
              <Controller
                name={`envPermissions.${index}.defaultResourcePermission`}
                control={control}
                render={({ field }) => (
                  <PermissionRow>
                    <SectionLabelWithIcon>
                      <SectionLabel>
                        Resource permissions{' '}
                        {filterGroups.length
                          ? `(${filterGroups.length})`
                          : null}
                      </SectionLabel>
                      <Tooltip
                        title={
                          <>
                            Defines access to assets and tests in the selected
                            filter group
                          </>
                        }
                      >
                        <InfoIconWrapper>
                          <SvgIcon width={16}>
                            <InfoIcon />
                          </SvgIcon>
                        </InfoIconWrapper>
                      </Tooltip>
                    </SectionLabelWithIcon>
                    <SectionLabel $secondary>
                      Resources are by default
                    </SectionLabel>
                    <PermissionsAccessDropdown
                      value={field.value || defaultAccess}
                      onChange={field.onChange}
                    />
                  </PermissionRow>
                )}
              />
              <Controller
                name={`envPermissions.${index}.resourceFilterGroups`}
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value) return true;
                    const hasEmptyGroup = value.some(
                      (group) => group.filters.length === 0,
                    );
                    return (
                      !hasEmptyGroup ||
                      'All filter groups must have at least one filter'
                    );
                  },
                }}
                render={({ field }) => (
                  <ResourceFilterGroupsController
                    field={field}
                    filterOptions={filterOptions}
                    defaultAccess={defaultAccess}
                  />
                )}
              />
            </PermissionSection>
          </FlexColumn>
        </AccordionDetails>
      </Accordion>
    </AccordionEnvironmentItemWrapper>
  );
};
